<!-- src/views/Shift/Shift.vue -->
<template>
  <div>
    <v-progress-circular
        v-if="isLoading"
        :size="70"
        :width="7"
        color="primary"
        indeterminate
        class="centered"
    />
    <div v-else>
      <h1 class="text-center mt-3">เวลาทำงานของพนักงาน</h1>

      <div class="p-5 pb-0 pt-2">
        <div class="d-flex justify-content-end">
          <button class="btn btn-outline-primary" @click="uploadFiles">
            Import
          </button>

          <input
              id="uploadFileExcel"
              ref="uploadFileExcel"
              type="file"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              hidden
              @change="getFile"
          />
        </div>

        <div class="d-flex justify-content-end mt-3">
          <a href="#" target="_blank" @click.prevent="downloadSampleFile">
            Download Sample File
          </a>
        </div>
      </div>

      <div class="form shadow p-5 mb-5 bg-white rounded">
        <div class="row align-items-start">
          <div class="col-12 col-md-4 mb-3">
            <BrandDropdown
                :value="form.brand"
                :setDisable="false"
                :required="false"
                :styleObject="{ minWidth: '100%' }"
                @setBrand="(brand) => { form.brand = brand; }"
            />
            <div v-if="error.errorBrand" class="text-error">
              {{ error.errorBrand.message || 'กรุณาเลือก' }}
            </div>
          </div>

          <div class="col-12 col-md-8 mb-3">
            <label for="storeMultiSelect" class="form-check-label">Stores</label>
            <multiselect
                id="storeMultiSelect"
                v-model="form.stores"
                :options="listStores"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Pick some"
                track-by="storeCode"
                label="displayName"
                :disabled="isStoreMultiselectDisabled"
            />
            <div v-if="error.errorStoreCodes" class="text-error">
              {{ error.errorStoreCodes.message || 'กรุณาเลือก' }}
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-4 mb-3" :class="{'has-error': !!error.errorYear}">
            <label for="yearDropdown" required>Year</label>
            <multiselect
                id="yearDropdown"
                v-model="yearSelected"
                :options="years"
                :allow-empty="true"
                deselect-label=""
                placeholder="Select Year"
            />
            <div v-if="error.errorYear" class="text-error">
              {{ error.errorYear.message || 'กรุณาเลือก' }}
            </div>
          </div>

          <div class="col-12 col-md-4 mb-3" :class="{'has-error': !!error.errorMonth}">
            <label for="monthDropdown" required>Month</label>
            <multiselect
                id="monthDropdown"
                v-model="monthSelected"
                :options="monthsWithDisabled"
                label="text"
                track-by="id"
                :allow-empty="true"
                deselect-label=""
                placeholder="Select Month"
                :option-disabled="isMonthDisabled"
                :disabled="!yearSelected"
            />
            <div v-if="error.errorMonth" class="text-error">
              {{ error.errorMonth.message || 'กรุณาเลือก' }}
            </div>
          </div>

          <div class="col-12 col-md-4 mb-3 d-flex align-items-end gap-2">
            <button class="btn btn-outline-secondary flex-fill" @click="resetFilter">
              Reset
            </button>
            <button class="btn btn-outline-primary flex-fill" @click="exportShifts">
              Export
            </button>
          </div>
        </div>

        <div
            v-if="importResult.visible"
            class="result-card shadow p-4 mt-10 mb-4 bg-white rounded position-relative"
            style="max-width: 720px"
        >
          <button type="button" class="close-btn" aria-label="Close" @click="importResult.visible = false">
            ×
          </button>

          <h3 class="mb-4"><strong>สรุปผลการนำเข้าข้อมูล</strong></h3>

          <div class="d-flex justify-content-between mb-2">
            <span>ทำรายการสำเร็จ</span>
            <span>{{ importResult.successCount }} รายการ</span>
          </div>
          <div class="d-flex justify-content-between mb-4">
            <span>ทำรายการไม่สำเร็จ ข้อมูลไม่ถูกต้อง</span>
            <span>{{ importResult.failedCount }} รายการ</span>
          </div>

          <div class="mb-2"><strong>ปัญหาที่พบ</strong></div>
          <div class="mb-3">{{ importResult.message || '-' }}</div>

          <div class="mb-2 d-flex font-weight-bold">
            <div class="error-col-field text-center">บรรทัดที่</div>
            <div class="error-col-msg">ปัญหาที่พบ</div>
          </div>

          <div v-if="(importResult.errors || []).length === 0" class="text-muted">— ไม่มีข้อผิดพลาด —</div>
          <ul v-else class="list-unstyled mb-0">
            <li v-for="(err, idx) in importResult.errors" :key="idx" class="d-flex mb-1">
              <div class="error-col-field text-center">{{ err.fieldName }}</div>
              <div class="error-col-msg">{{ err.message }}</div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <v-dialog v-model="showDialog" width="500" persistent>
      <div class="content-dialog p-5 bg-white rounded">
        <h3 class="mb-4"><strong>{{ message }}</strong></h3>
        <div class="row text-center">
          <div class="col">
            <button class="btn-back btn btn-outline-secondary" @click="showDialog = false">ปิด</button>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import moment from "moment";

import PresignedUploadUrlService from "services/api/presignedUploadUrl.service";
import ShiftService from "services/api/shift.service";
import StoreService from "services/api/store.service";
import BrandDropdown from "components/Brand/BrandDropdown.vue";

export default {
  name: "ShiftPage",
  components: {BrandDropdown, Multiselect },
  data() {
    return {
      isLoading: true,
      form: {
        brand: null,
        stores: [],
      },
      listBrands: [],
      listStores: [],
      years: [],
      months: [],
      yearSelected: moment().format("YYYY"),
      monthSelected: null,
      isSubmit: false,
      error: {
        errorMonth: null,
        errorYear: null,
        errorBrand: null,
        errorStoreCodes: null,
      },
      importResult: {
        visible: false,
        successCount: 0,
        failedCount: 0,
        message: "",
        errors: [],
      },
      message: null,
      showDialog: false,
    };
  },
  async mounted() {
    try {
      await this.getListStores();
      this.setYears();
      this.setMonths();
    } finally {
      this.isLoading = false;
    }
  },
  computed: {
    isStoreMultiselectDisabled() {
      return !this.form.brand;
    },
    currentYearNum() {
      return Number.parseInt(moment().format("YYYY"), 10);
    },
    currentMonthNum() {
      return Number.parseInt(moment().format("M"), 10);
    },
    monthsWithDisabled() {
      if (!this.yearSelected) {
        return this.months.map(m => ({ ...m, disabled: true }));
      }
      const year = Number.parseInt(this.yearSelected, 10);
      if (Number.isNaN(year)) {
        return this.months.map(m => ({ ...m, disabled: true }));
      }

      return this.months.map((month) => {
        if (year === this.currentYearNum) return { ...month, disabled: month.id >= this.currentMonthNum };
        if (year < this.currentYearNum) return { ...month, disabled: false };
        return { ...month, disabled: true };
      });
    },

  },
  watch: {
    "form.brand"() {
      this.form.stores = [];
      this.getListStores();
    },
    yearSelected(newVal) {
      if (!newVal) {
        this.monthSelected = null;
        return;
      }
      const year = Number.parseInt(newVal, 10);
      if (year > this.currentYearNum) {
        this.monthSelected = null;
        return;
      }
      if (year === this.currentYearNum) {
        const prevMonth = this.currentMonthNum - 1;
        this.monthSelected = prevMonth >= 1 ? this.months.find((month) => month.id === prevMonth) || null : null;
        return;
      }
      if (!this.monthSelected) {
        this.monthSelected = this.months.find((month) => month.id === 12) || null;
      }
    },
  },
  methods: {
    uploadFiles() {
      this.$refs.uploadFileExcel?.click();
    },
    getFile(e) {
      const file = e?.target?.files?.[0];
      if (!file) return;
      this.uploadExcel(file, e);
    },
    async uploadExcel(file, e) {
      try {
        this.isLoading = true;
        this.importResult.visible = false;

        const sign = await PresignedUploadUrlService.getS3SignUrl("shifts", file);
        const signedData = sign?.data;
        if (!signedData?.presignedUrl || !signedData?.key) {
          throw new Error("ไม่สามารถขอลิงก์อัปโหลดได้");
        }

        await PresignedUploadUrlService.pushProfilePhotoToS3(signedData.presignedUrl, file);

        const importRes = await ShiftService.importFromS3Key(signedData.key);
        const payload = importRes?.data || { errors: [], successCount: 0, failedCount: 0, message: "" };

        const errors = Array.isArray(payload?.errors)
            ? payload.errors.map((ee) => ({
              fieldName: ee?.rowIndex ?? ee?.fieldName ?? "-",
              message: ee?.message ?? "-",
            }))
            : [];

        this.importResult = {
          visible: true,
          successCount: Number(payload.successCount ?? 0),
          failedCount: Number(payload.failedCount ?? errors.length ?? 0),
          message: payload.message ?? "",
          errors,
        };
      } catch (err) {
        console.log("Error uploadExcel:", err);
        this.message =
            err?.response?.data?.message || err?.message || "ข้อมูลของไฟล์ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง";
        this.showDialog = true;
      } finally {
        this.isLoading = false;
        if (e?.target) e.target.value = null;
      }
    },

    downloadSampleFile() {
      try {
        const base = process.env.VUE_APP_HOST_API || "";
        window.open(`${base}static/sample/sampleShifts.xlsx`, "_blank");
      } catch (err) {
        console.log("Error: ", err);
      }
    },

    async getListStores() {
      try {
        const brandIds = this.form.brand ? [this.form.brand.id] : [];
        const params = { brands: brandIds };
        const res = await StoreService.getListStore(params);
        const data = res?.data || {};
        const stores = Array.isArray(data.store) ? data.store : [];
        this.listStores = stores.map((i) => ({ storeCode: i.storeCode, displayName: i.displayName }));
      } catch (err) {
        console.log("Error getListStores:", err);
      }
    },

    setYears() {
      const currentYear = Number.parseInt(moment().format("YYYY"), 10);
      this.years = [];
      for (let y = currentYear; y >= 1950; y--) this.years.push(y.toString());
      if (!this.years.includes(this.yearSelected)) this.yearSelected = currentYear.toString();
    },

    setMonths() {
      this.months = moment.months().map((name, idx) => ({ id: idx + 1, text: name }));
      const current = moment();
      const currentMonthId = current.month() + 1;
      const currentYear = current.year();
      this.yearSelected = currentYear.toString();
      this.monthSelected = this.months.find((m) => m.id === currentMonthId) || null;
    },

    isMonthDisabled(opt) {
      return !!opt?.disabled;
    },

    resetFilter() {
      this.form.brand = null;
      this.form.stores = [];
      if (!this.years?.length) this.setYears();
      if (!this.months?.length) this.setMonths();

      this.yearSelected = null;
      this.monthSelected = null;
      this.isSubmit = false;
      this.error.errorMonth = null;
      this.error.errorYear = null;
    },

    async exportShifts() {
      const brandCode = this.form.brand?.id || null;
      const storeCodes = (this.form.stores || []).map((s) => s.storeCode);
      const year = this.yearSelected || null;
      const month = this.monthSelected?.id || null;

      const params = {};
      if (brandCode) params.brandCode = brandCode;
      if (storeCodes.length) params.storeCodes = storeCodes.join(",");
      if (year) params.year = year;
      if (month) params.month = month;

      try {
        this.isLoading = true;
        while (true) {
          const res = await ShiftService.exportShift(params);
          const data = res.data;

          if (data.url) {
            this.resetErrorText();
            window.open(data.url, "_blank");
            break;
          }

          if (data.errors.length > 0) {
            this.message = data.message || "Failed to generate report.";
            this.showDialog = true;
            break;
          }

          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      } catch (err) {
        const errorData = err?.response?.data;
        if (errorData?.errors?.length) {
          this.setErrorText(errorData.errors);
        }
      } finally {
        this.isLoading = false;
      }
    },
    setErrorText(errors) {
      this.error.errorBrand = errors.find((e) => e.fieldName === "brandCode") || null;
      this.error.errorStoreCodes = errors.find((e) => e.fieldName === "storeCodes") || null;
      this.error.errorYear = errors.find((e) => e.fieldName === "year") || null;
      this.error.errorMonth = errors.find((e) => e.fieldName === "month") || null;
    },
    resetErrorText() {
      this.error = {
        errorBrand: null,
        errorStoreCodes: null,
        errorMonth: null,
        errorYear: null,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.centered {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.form {
  min-height: 20vh;
  margin: 3rem 3rem;
  position: relative;
}

.rounded {
  border-radius: 1rem !important;
}

.text-error {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 4px;
}

.gap-2 > * + * {
  margin-left: 0.5rem;
}

.result-card {
  border: 1px solid #eee;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 16px;
  background: transparent;
  border: 0;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.error-col-field {
  width: 80px;
}

.error-col-msg {
  flex: 1;
  text-align: left;
}
</style>
